import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Renders its children into a SEPARATE browser window via a real React portal
 * (window.open + createPortal), so the content can be dragged to another screen
 * while staying part of the same React tree (state, context, i18n all intact).
 *
 * The child app's styles are cloned into the new window (Tailwind/theme tokens),
 * and the theme class on <html> is kept in sync. Closing the window (or unmount)
 * calls onClose so the parent can re-dock.
 */
function copyStyles(srcDoc, destDoc) {
  srcDoc.querySelectorAll('style, link[rel="stylesheet"]').forEach(node => {
    try {
      destDoc.head.appendChild(node.cloneNode(true));
    } catch {
      /* ignore individual style copy failures */
    }
  });
}

export default function WindowPortal({ children, title = 'Chat', width = 460, height = 680, onClose }) {
  const [container, setContainer] = useState(null);

  useEffect(() => {
    const features = `popup=yes,width=${width},height=${height},left=${(window.screenX || 0) + 120},top=${(window.screenY || 0) + 80}`;
    const win = window.open('', '', features);
    if (!win) {
      // Popup blocked — fall back to re-docking so the chat is never lost.
      onClose?.();
      return undefined;
    }

    win.document.title = title;
    const doc = win.document;
    // Reset default margins and give the portal root a full-window flex layout.
    doc.body.style.margin = '0';
    doc.body.style.height = '100vh';
    doc.body.style.overflow = 'hidden';
    copyStyles(document, doc);
    doc.documentElement.className = document.documentElement.className; // dark/light + theme

    const mount = doc.createElement('div');
    mount.style.height = '100vh';
    doc.body.appendChild(mount);
    setContainer(mount);

    const handleClose = () => onClose?.();
    win.addEventListener('beforeunload', handleClose);

    return () => {
      win.removeEventListener('beforeunload', handleClose);
      try { win.close(); } catch { /* already closed */ }
    };
    // Open exactly once for this portal instance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return container ? createPortal(children, container) : null;
}
